import {
  DataGridPro,
  GridColDef,
  GridDensity,
  GridEventListener,
  GridFilterModel,
  GridInitialState,
  GridProSlotsComponent,
  GridRowHeightParams,
  GridRowHeightReturnValue,
  GridRowIdGetter,
  GridLogicOperator,
  FilterColumnsArgs,
  GridRowSelectionModel,
  GridRowId,
  GridSlotProps,
  GridRowParams,
  GridApiPro,
  GridSortModel,
  GridCallbackDetails,
  GridPaginationModel,
} from '@mui/x-data-grid-pro';
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useMemo,
} from 'react';
import { FieldValues } from 'react-hook-form';
import { BigQueryPage, Page } from '../../types';
import {
  getSharedDataGridFilterTheme,
  getSharedDataGridTheme,
} from './shared-datagrid-theme';
import { useUserSettings } from '../utils/use-user-settings';
import { isFeatureEnabled } from '../utils/feature-flags';
import CustomFilterPanel from './datagrids/custom-filter-panel';
import CustomColumnsPanel from './datagrids/custom-columns-panel';
import { FilterPanelDeleteIcon } from './datagrids/shared-datagrids-utils';
import { useTheme, type SxProps } from '@mui/material/styles';
interface SharedDataGridServerProps<
  Model extends FieldValues,
  SelectedRowsType extends GridRowId,
> {
  columns: GridColDef[];
  data?: Page<Model[]> | BigQueryPage<Model[]>;
  initialState?: GridInitialState;
  slots: Partial<GridProSlotsComponent>;
  slotProps?: Partial<GridSlotProps>;
  selectedRows?: GridRowId[];
  setSelectedRows?: Dispatch<SetStateAction<SelectedRowsType[]>>;
  onRowClick?: GridEventListener<'rowClick'>;
  checkboxSelection?: boolean;
  disableRowSelectionOnClick?: boolean;
  disableColumnSelector?: boolean;
  gridKey?: string | null;
  showToolbar?: boolean;
  density?: GridDensity;
  getRowId?: GridRowIdGetter<Model>;
  sx?: SxProps;
  filterPanelSx?: SxProps;
  getRowHeight?: (params: GridRowHeightParams) => GridRowHeightReturnValue;
  errorMessageLabel?: string;
  toolbarQuickFilterWidth?: number;
  pageSizeOptions?: (
    | number
    | {
        value: number;
        label: string;
      }
  )[];
  onRowSelectionModelChange?:
    | ((rowSelectionModel: GridRowSelectionModel) => void)
    | undefined;
  filterOperator?: GridLogicOperator[];
  disableColumnMenu?: boolean;
  getDetailPanelContent?: (params: GridRowParams<Model>) => React.ReactNode;
  getDetailPanelHeight?: (params: GridRowParams<Model>) => number | 'auto';
  apiRef?: RefObject<GridApiPro | null>;
  setIsLoading?: Dispatch<SetStateAction<boolean>>;
  keepNonExistentRowsSelected?: boolean;
  loading?: boolean;
  onRowsScrollEnd?: GridEventListener<'rowsScrollEnd'>;
  rows?: Model[];
  paginationModel?: GridPaginationModel;
  onPaginationModelChange?: (
    model: GridPaginationModel,
    details: GridCallbackDetails<'pagination'>
  ) => void;
  filterModel?: GridFilterModel;
  onFilterModelChange?: (
    model: GridFilterModel,
    details: GridCallbackDetails<'filter'>
  ) => void;
  sortModel?: GridSortModel;
  onSortModelChange?: (
    model: GridSortModel,
    details: GridCallbackDetails
  ) => void;
  rowCount?: number;
  isRowSelectable?: (params: GridRowParams<Model>) => boolean;
  sortingMode?: 'client' | 'server';
  filterMode?: 'client' | 'server';
}

export default function SharedDataGridServer<
  Model extends FieldValues,
  SelectedRowsType extends GridRowId = GridRowId,
>({
  getRowId,
  columns,
  slots,
  slotProps,
  selectedRows,
  setSelectedRows,
  onRowClick,
  checkboxSelection = false,
  disableRowSelectionOnClick = false,
  disableColumnSelector = false,
  gridKey,
  initialState,
  showToolbar = true,
  density,
  sx,
  filterPanelSx,
  getRowHeight,
  onRowSelectionModelChange,
  filterOperator,
  disableColumnMenu = false,
  toolbarQuickFilterWidth = 300,
  pageSizeOptions,
  getDetailPanelContent,
  getDetailPanelHeight,
  apiRef,
  keepNonExistentRowsSelected = true,
  loading = false,
  onRowsScrollEnd,
  rows,
  paginationModel,
  onPaginationModelChange,
  filterModel,
  onFilterModelChange,
  sortModel,
  onSortModelChange,
  data,
  rowCount,
  isRowSelectable,
  sortingMode = 'server',
  filterMode = 'server',
}: SharedDataGridServerProps<Model, SelectedRowsType>) {
  const theme = useTheme();
  const { features } = useUserSettings();
  const rowSelectionModel = useMemo(
    () => ({
      type: 'include' as const,
      ids: new Set(selectedRows ?? []),
    }),
    [selectedRows]
  );

  const filterColumns = ({
    field,
    columns,
    currentFilters,
  }: FilterColumnsArgs) => {
    const filteredFields = currentFilters?.map((item) => item.field);
    return columns
      .filter(
        (colDef) =>
          colDef.filterable &&
          (colDef.field === field || !filteredFields.includes(colDef.field))
      )
      .map((column) => column.field);
  };

  const calculateRowCount = useCallback(() => {
    if (!data) return -1;

    if ('totalCount' in data) {
      return data.totalCount;
    }

    if ('cursor' in data) {
      if (!data.cursor || data.cursor === '') {
        return (
          (data.results.length ?? 0) +
          (paginationModel?.page ?? 0) * (paginationModel?.pageSize ?? 0)
        );
      }

      return -1;
    }

    return -1;
  }, [data, paginationModel?.page, paginationModel?.pageSize]);

  const uiEnhancementPhase2FeatureEnabled = isFeatureEnabled(
    'uiEnhancementPhase2',
    features
  );

  const panelSlots = useMemo(
    () =>
      uiEnhancementPhase2FeatureEnabled
        ? {
            filterPanel: CustomFilterPanel,
            filterPanelDeleteIcon: FilterPanelDeleteIcon,
            columnsPanel: CustomColumnsPanel,
          }
        : undefined,
    [uiEnhancementPhase2FeatureEnabled]
  );

  const mergedSlots = useMemo(
    () => ({ ...slots, ...panelSlots }),
    [slots, panelSlots]
  );

  const filterPanelInputVariant = uiEnhancementPhase2FeatureEnabled
    ? 'outlined'
    : 'standard';

  return (
    <DataGridPro
      apiRef={apiRef}
      showToolbar={showToolbar}
      loading={loading}
      rows={rows ?? data?.results ?? []}
      key={gridKey}
      getRowId={getRowId}
      density={density ?? 'standard'}
      slots={mergedSlots}
      slotProps={{
        ...slotProps,
        columnsPanel: {
          sx: {
            '& .MuiDataGrid-columnsManagementHeader': {
              borderBottom: 'unset',
            },
            '& .MuiFormControlLabel-label': {
              fontSize: '1rem !important',
            },
          },
        },
        filterPanel: {
          logicOperators: filterOperator,
          filterFormProps: {
            logicOperatorInputProps: {
              variant: filterPanelInputVariant,
              size: 'small',
            },
            columnInputProps: {
              variant: filterPanelInputVariant,
              size: 'small',
            },
            operatorInputProps: {
              variant: filterPanelInputVariant,
              size: 'small',
            },
            valueInputProps: {
              InputComponentProps: {
                variant: filterPanelInputVariant,
              },
            },
            filterColumns,
          },
          sx: {
            ...getSharedDataGridFilterTheme(filterPanelSx),
          },
        },
      }}
      columnHeaderHeight={60}
      rowCount={rowCount ?? calculateRowCount()}
      getRowHeight={getRowHeight}
      rowHeight={60}
      checkboxSelection={checkboxSelection}
      disableRowSelectionOnClick={disableRowSelectionOnClick}
      disableColumnSelector={disableColumnSelector}
      keepNonExistentRowsSelected={keepNonExistentRowsSelected}
      disableRowSelectionExcludeModel
      columns={columns}
      disableColumnMenu={disableColumnMenu}
      localeText={{
        paginationDisplayedRows: ({ from, to, count }) =>
          `${from} - ${to} of ${count === -1 ? 'many' : count}`,
        filterPanelRemoveAll: uiEnhancementPhase2FeatureEnabled
          ? 'Reset'
          : 'Remove All',
        filterPanelAddFilter: 'Add Filter',
      }}
      pageSizeOptions={pageSizeOptions ? pageSizeOptions : [25, 50, 100]}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      filterMode={filterMode}
      filterModel={filterModel}
      onFilterModelChange={onFilterModelChange}
      sortingMode={sortingMode}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      rowSelectionModel={rowSelectionModel}
      onRowSelectionModelChange={(newModel) => {
        if (!setSelectedRows) return;
        const ids = Array.from(newModel.ids) as SelectedRowsType[];
        setSelectedRows(ids);
        if (onRowSelectionModelChange) {
          onRowSelectionModelChange(newModel);
        }
      }}
      initialState={initialState ?? {}}
      sx={{
        ...getSharedDataGridTheme(theme, {
          toolbarQuickFilterWidth,
          sx,
        }),
      }}
      onRowClick={onRowClick}
      getDetailPanelContent={getDetailPanelContent}
      getDetailPanelHeight={getDetailPanelHeight}
      onRowsScrollEnd={onRowsScrollEnd}
      isRowSelectable={isRowSelectable}
    />
  );
}
